import { type MouseEvent } from 'react';

import { colors } from '../../colors';
import { ComparisonIcon, LayoutIcon, GoalIcon, XCircleIcon } from '../icons';
import { Heading3, Heading4 } from '../Text';

import * as css from './PropertyTrigger.css';

const availableIcons = {
    layout: { component: LayoutIcon, size: 15 },
    comparison: { component: ComparisonIcon, size: 14 },
    goal: { component: GoalIcon, size: 14, color: 'currentColor' },
};

interface PropertyTriggerValue {
    label: string;
    icon?: 'layout' | 'comparison' | 'goal';
}

export interface PropertyTriggerProps {
    /** Main label displayed above the values. */
    label: string;

    /** List of selected values displayed inside the trigger. */
    values: PropertyTriggerValue[];

    /**
     * Click handler for the entire trigger.
     * When provided, the component behaves like a button.
     */
    onClick?: (event: MouseEvent) => void;

    /**
     * Optional clear handler.
     * When provided, a clear (X) button is rendered.
     */
    onClear?: () => void;

    /** Visual variant of the trigger. */
    variant?: 'default' | 'lighter' | 'darker';

    /**
     * When true, applies disabled styling and suppresses interaction.
     */
    disabled?: boolean;
}

function Value({ label, icon }: PropertyTriggerValue) {
    return (
        <div className={css.value}>
            <PropertyIcon icon={icon} />
            <Heading3 as="span">{label}</Heading3>
        </div>
    );
}

function PropertyIcon({ icon }: Pick<PropertyTriggerValue, 'icon'>) {
    if (!icon) {
        return;
    }
    const { component: Icon, ...rest } = availableIcons[icon];

    return (
        <span className={css.icon}>
            <Icon {...rest} />
        </span>
    );
}

export function PropertyTrigger({
    label = 'Label',
    values,
    onClick,
    onClear,
    disabled = false,
    variant,
}: PropertyTriggerProps) {
    const isClickable = typeof onClick === 'function';

    const handleClear = (event: MouseEvent) => {
        event.stopPropagation();
        onClear?.();
    };

    return (
        <div
            className={css.propertyTrigger({ isDisabled: disabled, variant })}
            role={isClickable ? 'button' : undefined}
            onClick={(event) => onClick?.(event)}
        >
            <div className={css.label}>
                <Heading4>{label}</Heading4>
            </div>
            {values.map((value) => (
                <Value key={value.label} label={value.label} icon={value.icon} />
            ))}
            {onClear && (
                <button className={css.clearButton} onClick={handleClear} type="button">
                    <XCircleIcon width={20} height={20} weight="fill" color={colors.blue[500]} />
                </button>
            )}
        </div>
    );
}
